/**
 * Costanti condivise della feature Generatore Batch.
 * Stati, etichette e mappe stile derivano 1:1 dal contratto backend
 * (backend/docs/generator-batch-api.md). Non introdurre stati non documentati.
 */

// Utente autorizzato (confronto senza distinzione maiuscole/minuscole).
export const AUTHORIZED_USERNAME = 'paul';

/**
 * Ricava lo username normalizzato dall'oggetto utente del contesto Auth.
 * Il backend autorizza su `UTE_NomeUtente`; teniamo alcuni fallback difensivi.
 */
export const normalizeUsername = (user) => {
  const raw =
    user?.UTE_NomeUtente ??
    user?.username ??
    user?.UTE_Username ??
    '';
  return String(raw).trim().toLowerCase();
};

export const isAuthorizedUser = (user) =>
  normalizeUsername(user) === AUTHORIZED_USERNAME;

// Passi del flusso guidato.
export const STEPS = [
  { id: 'source', label: 'Prodotto e moduli', short: 'Prodotto' },
  { id: 'recent', label: 'Modifiche recenti', short: 'Modifiche' },
  { id: 'skeletons', label: 'Scheletri', short: 'Scheletri' },
  { id: 'plan', label: 'Piano', short: 'Piano' },
  { id: 'run', label: 'Generazione', short: 'Generazione' },
  { id: 'results', label: 'Risultati', short: 'Risultati' },
];

// Stati batch documentati.
export const BATCH_STATUS = {
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SUCCESS: 'SUCCESS',
  PARTIAL: 'PARTIAL',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  INTERRUPTED: 'INTERRUPTED',
};

export const TERMINAL_BATCH_STATUSES = new Set([
  BATCH_STATUS.SUCCESS,
  BATCH_STATUS.PARTIAL,
  BATCH_STATUS.FAILED,
  BATCH_STATUS.CANCELLED,
  BATCH_STATUS.INTERRUPTED,
]);

export const isTerminalStatus = (status) => TERMINAL_BATCH_STATUSES.has(status);

// Stati task documentati.
export const TASK_STATUS = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  CANCELLED: 'CANCELLED',
  INTERRUPTED: 'INTERRUPTED',
};

/**
 * Classi Tailwind per i badge di stato (batch e task condividono la scala).
 * Coerenti con il tema chiaro (emerald/amber/red) e scuro (indigo/violet).
 */
export const STATUS_STYLES = {
  QUEUED:
    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  PENDING:
    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  RUNNING:
    'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
  SUCCESS:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  PARTIAL:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  FAILED:
    'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  CANCELLED:
    'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
  INTERRUPTED:
    'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
};

export const statusStyle = (status) =>
  STATUS_STYLES[status] || STATUS_STYLES.QUEUED;

// Etichette leggibili degli stati (IT).
export const STATUS_LABELS = {
  QUEUED: 'In coda',
  PENDING: 'In attesa',
  RUNNING: 'In esecuzione',
  SUCCESS: 'Completato',
  PARTIAL: 'Parziale',
  FAILED: 'Fallito',
  CANCELLED: 'Annullato',
  INTERRUPTED: 'Interrotto',
};

export const statusLabel = (status) => STATUS_LABELS[status] || status || '—';

// Motivo della modifica (badge origine) per le macrofunzioni recenti.
export const ORIGIN_META = {
  macro: {
    label: 'Macro',
    style: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300',
  },
  table: {
    label: 'Tabella',
    style: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  },
  fields: {
    label: 'Campi',
    style: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300',
  },
  operators: {
    label: 'Operatori',
    style: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300',
  },
  objects: {
    label: 'Oggetti',
    style: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/50 dark:text-cyan-300',
  },
  attributes: {
    label: 'Attributi',
    style: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
  },
  tableDeleted: {
    label: 'Tabella rimossa',
    style: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  },
  fieldsDeleted: {
    label: 'Campi rimossi',
    style: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  },
  operatorsDeleted: {
    label: 'Operatori rimossi',
    style: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
  },
  objectsDeleted: {
    label: 'Oggetti rimossi',
    style: 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
  },
  attributesDeleted: {
    label: 'Attributi rimossi',
    style: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  },
};

// --- Copertura generazione (stato generale su GET /recent) ------------------

export const REGENERATION_MODE = {
  OUTDATED_ONLY: 'OUTDATED_ONLY',
  ALL: 'ALL',
};

/**
 * Stato generale della generazione per una macrofunzione (item di /recent).
 * Volutamente NON conclusivo: la copertura esatta per gli scheletri scelti la
 * dà /plan. Per questo SUCCESS_AFTER_CHANGE non si chiama "Tutto aggiornato".
 */
export const RECENT_GEN_STATUS_META = {
  NO_SUCCESS: {
    label: 'Mai generata',
    style: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    hint: 'Nessuna generazione riuscita per questa tabella.',
  },
  CHANGED_AFTER_SUCCESS: {
    label: 'Da rigenerare',
    style: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
    hint: 'Esistono generazioni riuscite, ma nessuna copre la revisione corrente.',
  },
  SUCCESS_AFTER_CHANGE: {
    label: 'Generata dopo le modifiche',
    style: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
    hint: 'Stato generale: la copertura dei singoli scheletri sarà verificata nel piano.',
  },
};

export const recentGenMeta = (status) => RECENT_GEN_STATUS_META[status] || null;

/** Priorità di ordinamento: prima ciò che richiede attenzione. */
export const RECENT_GEN_PRIORITY = {
  NO_SUCCESS: 0,
  CHANGED_AFTER_SUCCESS: 1,
  SUCCESS_AFTER_CHANGE: 2,
};

export const recentGenPriority = (status) =>
  RECENT_GEN_PRIORITY[status] ?? 99;

// Filtri client-side sullo stato generale.
export const RECENT_STATUS_FILTERS = [
  { id: 'all', label: 'Tutte', statuses: null },
  {
    id: 'to_generate',
    label: 'Da generare',
    statuses: ['NO_SUCCESS', 'CHANGED_AFTER_SUCCESS'],
  },
  {
    id: 'generated',
    label: 'Generate dopo la modifica',
    statuses: ['SUCCESS_AFTER_CHANGE'],
  },
];

/** Stati che rientrano in "da generare" (per la selezione mirata). */
export const TO_GENERATE_STATUSES = new Set(['NO_SUCCESS', 'CHANGED_AFTER_SUCCESS']);

// --- Copertura per singolo task (tasks[].generation.status su /plan) --------

export const PLAN_TASK_GEN_STATUS_META = {
  NEVER_GENERATED: {
    label: 'Mai generato',
    style: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  },
  OUTDATED: {
    label: 'Da generare',
    style: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  },
  UP_TO_DATE_INFERRED: {
    label: 'Storico da verificare',
    style: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
  },
  UP_TO_DATE: {
    label: 'Aggiornato',
    style: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
};

export const planTaskGenMeta = (status) => PLAN_TASK_GEN_STATUS_META[status] || null;

// --- Dettaglio modifiche (GET /recent/:id/changes) --------------------------

// Azione su singola entità nel dettaglio.
export const CHANGE_ACTION_META = {
  CREATE: {
    label: 'Aggiunto',
    style: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  },
  UPDATE: {
    label: 'Modificato',
    style: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  },
  DELETE: {
    label: 'Rimosso',
    style: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  },
};

export const changeActionMeta = (action) => CHANGE_ACTION_META[action] || null;

// Azione a livello di sorgente in changeSummary.sources.
export const SOURCE_ACTION_LABELS = {
  CHANGE: 'Modifica',
  DELETE: 'Rimozione',
};

// Livelli di log accettati dal contratto.
export const LOG_LEVELS = ['DEBUG', 'INFO', 'WARNING', 'NONE'];

// Preset per il filtro temporale delle modifiche recenti.
export const TIME_PRESETS = [
  { id: 'all', label: 'Tutte' },
  { id: '7d', label: 'Ultimi 7 giorni', days: 7 },
  { id: '30d', label: 'Ultimi 30 giorni', days: 30 },
  { id: '90d', label: 'Ultimi 90 giorni', days: 90 },
  { id: 'custom', label: 'Intervallo…' },
];
